/*
  Copyright (c) 2018-present, Accuracy.
  All rights reserved.
*/
import {createStyles, StyleRules, Theme} from "@material-ui/core";
import makeStyles from '@material-ui/core/styles/makeStyles';
import classNames from 'classnames';
import React, {memo, useState} from 'react';
import {useTranslation} from "react-i18next";
import {Link, useLocation} from "react-router-dom";

//----------------------------------------------------------------------------------------------------------------------
//- Styles
//----------------------------------------------------------------------------------------------------------------------
// Below this width the nav links + lang switch no longer fit next to the
// wordmark on one line (checked against the longest FR labels), so they
// move into a collapsible mobile panel instead.
const MOBILE_BREAKPOINT = 880;

export type TopBarClassKey = 'root' | 'wordmark' | 'menuButton' | 'nav' | 'navLink' | 'langGroup' | 'langButton' | 'langDivider' | 'mobileNav' | 'mobileNavOpen' | 'mobileNavLink' | 'mobileLangGroup';

const useStyles = makeStyles<Theme, {}, TopBarClassKey>(
  (): StyleRules<TopBarClassKey> =>
    createStyles({
      root: {
        position: 'sticky',
        top: 0,
        zIndex: 200,
        height: '66px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: '24px',
        padding: '0 clamp(20px, 4vw, 56px)',
        background: 'rgba(11,11,12,.72)',
        backdropFilter: 'blur(14px)',
        WebkitBackdropFilter: 'blur(14px)',
        borderBottom: '1px solid var(--color-rule-header)',
        boxSizing: 'border-box'
      },
      wordmark: {
        background: 'none',
        border: 'none',
        padding: 0,
        cursor: 'pointer',
        color: 'var(--color-text)',
        fontFamily: 'var(--font-body)',
        fontSize: '13px',
        fontWeight: 500,
        letterSpacing: '.24em',
        textTransform: 'uppercase',
        textDecoration: 'none',
        whiteSpace: 'nowrap'
      },
      menuButton: {
        display: 'none',
        background: 'none',
        border: '1px solid var(--color-border-soft)',
        padding: '7px 11px',
        cursor: 'pointer',
        color: 'var(--color-text)',
        fontFamily: 'var(--font-body)',
        fontSize: '14px',
        lineHeight: 1,
        [`@media (max-width: ${MOBILE_BREAKPOINT}px)`]: {
          display: 'block'
        }
      },
      nav: {
        display: 'flex',
        alignItems: 'center',
        gap: 'clamp(14px, 2.4vw, 32px)',
        [`@media (max-width: ${MOBILE_BREAKPOINT}px)`]: {
          display: 'none'
        }
      },
      navLink: {
        background: 'none',
        border: 'none',
        padding: '6px 0',
        cursor: 'pointer',
        color: 'var(--color-text-muted)',
        fontFamily: 'var(--font-body)',
        fontSize: '11.5px',
        letterSpacing: '.16em',
        textTransform: 'uppercase',
        textDecoration: 'none',
        whiteSpace: 'nowrap',
        transition: 'color .2s',
        '&:hover': {
          color: 'var(--color-text-strong)'
        }
      },
      langGroup: {
        display: 'flex',
        alignItems: 'center',
        gap: '6px',
        paddingLeft: 'clamp(8px, 1.5vw, 18px)',
        borderLeft: '1px solid rgba(255,255,255,.14)'
      },
      langButton: {
        background: 'none',
        border: 'none',
        padding: '2px',
        cursor: 'pointer',
        fontFamily: 'var(--font-body)',
        fontSize: '11px',
        letterSpacing: '.1em'
      },
      langDivider: {
        color: 'rgba(255,255,255,.22)',
        fontSize: '10px'
      },
      // Mobile dropdown panel: hidden by default, only reachable (and only
      // rendered in the layout) below the breakpoint — display:none both
      // collapses it out of the way on desktop and keeps its links out of
      // the tab order there, regardless of the `open` state.
      mobileNav: {
        display: 'none',
        [`@media (max-width: ${MOBILE_BREAKPOINT}px)`]: {
          display: 'none',
          position: 'fixed',
          top: '66px',
          left: 0,
          right: 0,
          zIndex: 199,
          flexDirection: 'column',
          // Fully opaque rather than the header's translucent-blur treatment:
          // this panel overlays page content people need to read past
          // (headline, hero image), so it shouldn't let any of it show through.
          background: 'var(--color-bg)',
          borderBottom: '1px solid var(--color-rule-header)',
          padding: '8px clamp(20px, 4vw, 56px) 20px',
          maxHeight: 'calc(100vh - 66px)',
          overflowY: 'auto'
        }
      },
      mobileNavOpen: {
        [`@media (max-width: ${MOBILE_BREAKPOINT}px)`]: {
          display: 'flex'
        }
      },
      mobileNavLink: {
        padding: '14px 0',
        borderBottom: '1px solid var(--color-rule)',
        color: 'var(--color-text-muted)',
        fontFamily: 'var(--font-body)',
        fontSize: '13px',
        letterSpacing: '.14em',
        textTransform: 'uppercase',
        textDecoration: 'none',
        '&:hover': {
          color: 'var(--color-text-strong)'
        }
      },
      mobileLangGroup: {
        display: 'flex',
        alignItems: 'center',
        gap: '10px',
        paddingTop: '18px'
      }
    }),
  {
    name: 'TopBar'
  });

//----------------------------------------------------------------------------------------------------------------------
//- Component
//----------------------------------------------------------------------------------------------------------------------
export type TopBarProps = {
  className?: string;
};

export default memo(({className}: TopBarProps) => {
  // hooks
  const classes = useStyles({});
  const {t, i18n} = useTranslation('common');
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);

  const currentLang = i18n.language || 'fr';

  const links = [
    {to: '/galleries', label: t('topBarMenu.galleries')},
    {to: '/about', label: t('topBarMenu.aboutMe')},
    {to: '/expos', label: t('topBarMenu.expositions')},
    {to: '/comments', label: t('topBarMenu.guestBook')},
    {to: '/links', label: t('topBarMenu.links')},
  ];

  // A route change (nav link click) should always close the mobile panel;
  // toggling the language shouldn't, since it doesn't navigate away.
  React.useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  return (
    <header className={classNames(classes.root, className)}>
      <Link to="/" className={classes.wordmark}>Pierre Castay</Link>

      <nav className={classes.nav}>
        {links.map((link) => (
          <Link key={link.to} to={link.to} className={classes.navLink}>{link.label}</Link>
        ))}
        <div className={classes.langGroup}>
          <button
            className={classes.langButton}
            style={{color: currentLang.startsWith('fr') ? 'var(--color-text-strong)' : 'rgba(232,230,226,.38)'}}
            onClick={() => i18n.changeLanguage('fr')}
          >
            FR
          </button>
          <span className={classes.langDivider}>/</span>
          <button
            className={classes.langButton}
            style={{color: currentLang.startsWith('en') ? 'var(--color-text-strong)' : 'rgba(232,230,226,.38)'}}
            onClick={() => i18n.changeLanguage('en')}
          >
            EN
          </button>
        </div>
      </nav>

      <button
        className={classes.menuButton}
        aria-label={mobileOpen ? t('topBarMenu.closeMenu') : t('topBarMenu.openMenu')}
        aria-expanded={mobileOpen}
        aria-controls="mobile-nav-panel"
        onClick={() => setMobileOpen((open) => !open)}
      >
        {mobileOpen ? '✕' : '☰'}
      </button>

      <nav id="mobile-nav-panel" className={classNames(classes.mobileNav, mobileOpen && classes.mobileNavOpen)}>
        {links.map((link) => (
          <Link key={link.to} to={link.to} className={classes.mobileNavLink}>{link.label}</Link>
        ))}
        <div className={classes.mobileLangGroup}>
          <button
            className={classes.langButton}
            style={{color: currentLang.startsWith('fr') ? 'var(--color-text-strong)' : 'rgba(232,230,226,.38)'}}
            onClick={() => i18n.changeLanguage('fr')}
          >
            FR
          </button>
          <span className={classes.langDivider}>/</span>
          <button
            className={classes.langButton}
            style={{color: currentLang.startsWith('en') ? 'var(--color-text-strong)' : 'rgba(232,230,226,.38)'}}
            onClick={() => i18n.changeLanguage('en')}
          >
            EN
          </button>
        </div>
      </nav>
    </header>
  );
});
